import * as z from "zod";

/**
 * Parsers for the three `get_return_data` tool output shapes. Each
 * returns `null` on mismatch so the renderer can fall back to the
 * generic JSON viewer.
 */

export const ReturnDataErrorSchema = z.object({ error: z.string() });
export type ReturnDataError = z.infer<typeof ReturnDataErrorSchema>;

export const ReturnDataDisambiguationSchema = z.object({
  needsDisambiguation: z.literal(true),
  orderId: z.string(),
  message: z.string(),
  returns: z.array(z.string()),
});
export type ReturnDataDisambiguation = z.infer<
  typeof ReturnDataDisambiguationSchema
>;

const CustomerSchema = z
  .object({
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    email: z.string().nullish(),
    // Legacy Shopify-customer rows use snake_case keys.
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
  })
  .passthrough();

const ProductSchema = z
  .object({
    product_title: z.string().nullish(),
    variant_title: z.string().nullish(),
    quantity: z.number().nullish(),
    return_reason: z.string().nullish(),
    strategy: z.string().nullish(),
  })
  .passthrough();

const MoneyEntrySchema = z
  .object({
    // Wire codec ships amounts as number or stringified decimal.
    amount: z.union([z.number(), z.string()]).nullish(),
  })
  .passthrough();

const OrderSummarySchema = z
  .object({
    name: z.string().nullish(),
    number: z.union([z.number(), z.string()]).nullish(),
    order: z.string().nullish(),
    orderedAt: z.string().nullish(),
  })
  .passthrough();

const PaymentIntentSchema = z
  .object({
    amount: z.union([z.number(), z.string()]).nullish(),
    status: z.string().nullish(),
  })
  .passthrough();

const AdvancedExchangeItemSchema = z
  .object({ quantity: z.number().nullish() })
  .passthrough();

const NotesSchema = z
  .object({
    notesToCustomer: z.string().nullish(),
    toCustomerOnApproval: z.string().nullish(),
    toCustomerOnProcessing: z.string().nullish(),
  })
  .passthrough();

export const ReturnDocSchema = z
  .object({
    _id: z.string(),
    status: z.string().nullish(),
    type: z.string().nullish(),
    createdAt: z.string().nullish(),
    completedAt: z.string().nullish(),
    approvedAt: z.string().nullish(),
    rejectType: z.string().nullish(),
    customer: CustomerSchema.nullish(),
    products: z.array(ProductSchema).nullish(),
    refunds: z.array(MoneyEntrySchema).nullish(),
    storedCredits: z.array(MoneyEntrySchema).nullish(),
    paymentIntents: z.array(PaymentIntentSchema).nullish(),
    advancedExchangeItems: z.array(AdvancedExchangeItemSchema).nullish(),
    orders: z.array(OrderSummarySchema).nullish(),
    returnType: z.array(z.string()).nullish(),
    currency: z.string().nullish(),
    notes: NotesSchema.nullish(),
    merchantCreated: z.boolean().nullish(),
  })
  .passthrough();
export type ReturnDoc = z.infer<typeof ReturnDocSchema>;

const SuccessOutputSchema = z.object({ returnData: z.string() }).passthrough();

/**
 * The agent tool wraps return JSON in `<return-data>...</return-data>`
 * for the LLM's benefit AND prepends a chunk of
 * `RETURN_DATA_DOCUMENTATION` prose above the tag. The docs prose
 * literally contains the text "<return-data>" as a field reference
 * (see return-data-context.ts line 3 — "Field reference for the
 * <return-data> JSON below..."). A naive `<return-data>(.*?)</return-data>`
 * matches FROM that prose mention TO the real closing tag, picking
 * up all the documentation + JSON and starting at prose — JSON.parse
 * then chokes on the prose preamble.
 *
 * Anchor the body to start with a JSON object `{` so the prose
 * mention (followed by a space + word) doesn't match — only the
 * real tag (followed by whitespace + a JSON object) does. The
 * trailing `}` capture is greedy enough to grab the full document
 * up to the closing tag.
 */
const RETURN_DATA_TAG = /<return-data>\s*(\{[\s\S]*\})\s*<\/return-data>/i;

export function tryParseReturnDoc(output: unknown): ReturnDoc | null {
  const wrapper = SuccessOutputSchema.safeParse(output);
  if (!wrapper.success) {
    return null;
  }
  const match = RETURN_DATA_TAG.exec(wrapper.data.returnData);
  if (!match) {
    return null;
  }
  const inner = match[1]?.trim();
  if (!inner) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(inner);
  } catch {
    return null;
  }

  const result = ReturnDocSchema.safeParse(parsed);
  return result.success ? result.data : null;
}

export function tryParseReturnError(output: unknown): ReturnDataError | null {
  const result = ReturnDataErrorSchema.safeParse(output);
  return result.success ? result.data : null;
}

export function tryParseReturnDisambiguation(
  output: unknown,
): ReturnDataDisambiguation | null {
  const result = ReturnDataDisambiguationSchema.safeParse(output);
  return result.success ? result.data : null;
}

/** Sum a list of money entries, ignoring null / non-numeric amounts. */
export function sumMoney(
  entries: readonly { amount?: number | string | null }[] | null | undefined,
): number | null {
  if (!entries || entries.length === 0) {
    return null;
  }
  let total = 0;
  let anyNumeric = false;
  for (const entry of entries) {
    const value = entry.amount;
    if (value == null) {
      continue;
    }
    const n = typeof value === "string" ? Number(value) : value;
    if (Number.isFinite(n)) {
      total += n;
      anyNumeric = true;
    }
  }
  return anyNumeric ? total : null;
}

export function formatCustomerName(
  customer: ReturnDoc["customer"] | null | undefined,
): string | null {
  if (!customer) {
    return null;
  }
  const first = (customer.firstName ?? customer.first_name ?? "").trim();
  const last = (customer.lastName ?? customer.last_name ?? "").trim();
  const full = `${first} ${last}`.trim();
  return full.length > 0 ? full : null;
}

export function totalItemCount(
  products: ReturnDoc["products"] | null | undefined,
): number {
  if (!products) {
    return 0;
  }
  return products.reduce((sum, p) => sum + (p.quantity ?? 0), 0);
}

export function primaryReturnReason(
  products: ReturnDoc["products"] | null | undefined,
): string | null {
  if (!products) {
    return null;
  }
  for (const p of products) {
    if (p.return_reason && p.return_reason.trim().length > 0) {
      return p.return_reason.trim();
    }
  }
  return null;
}

/**
 * Renderable order label, normalised to `#1234`. Some writers set
 * `name` (with leading #), others set bare `number`.
 */
export function primaryOrderLabel(
  orders: ReturnDoc["orders"] | null | undefined,
): string | null {
  if (!orders || orders.length === 0) {
    return null;
  }
  const first = orders[0];
  if (!first) {
    return null;
  }
  const name = first.name?.trim();
  if (name) {
    return name.startsWith("#") ? name : `#${name}`;
  }
  if (first.number != null) {
    return `#${first.number}`;
  }
  return null;
}

/** `"Tee — Medium / Blue"` for the first product line. */
export function firstProductLabel(
  products: ReturnDoc["products"] | null | undefined,
): string | null {
  if (!products || products.length === 0) {
    return null;
  }
  const first = products[0];
  if (!first) {
    return null;
  }
  const title = first.product_title?.trim() ?? "";
  const variant = first.variant_title?.trim() ?? "";
  if (title && variant) {
    return `${title} — ${variant}`;
  }
  return title || variant || null;
}

/**
 * For rejected returns: the merchant-authored note to the customer if
 * present, otherwise a prettified `rejectType` enum.
 */
export function rejectionExplanation(
  doc: Pick<ReturnDoc, "notes" | "rejectType" | "status">,
): string | null {
  if (doc.status !== "rejected") {
    return null;
  }
  const note = doc.notes?.notesToCustomer?.trim();
  if (note && note.length > 0) {
    return note;
  }
  if (doc.rejectType) {
    return prettifyEnum(doc.rejectType);
  }
  return null;
}

function prettifyEnum(value: string): string {
  const spaced = value.replace(/[-_]/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Count of exchange line items the customer is getting BACK (new items),
 * including direct variant exchanges and advanced exchanges (items that
 * weren't direct variants of returned items).
 */
export function newItemsCount(doc: ReturnDoc): number {
  let count = 0;
  for (const product of doc.products ?? []) {
    if (product.strategy === "exchange") {
      count += product.quantity ?? 0;
    }
  }
  for (const item of doc.advancedExchangeItems ?? []) {
    count += item.quantity ?? 0;
  }
  return count;
}

/**
 * Display labels for the compensation methods chosen on this return.
 * `returnType` is an array because a single return can mix strategies
 * (e.g. some products refunded, others exchanged). Returns each as a
 * separate badge label so the card surfaces all of them.
 */
export function compensationLabels(
  returnType: readonly string[] | null | undefined,
): string[] {
  if (!returnType || returnType.length === 0) {
    return [];
  }
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const value of returnType) {
    const label = compensationLabel(value);
    if (label && !seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  }
  return labels;
}

function compensationLabel(value: string): string | null {
  switch (value) {
    case "refund":
      return "Refund";
    case "store_credit":
      return "Store credit";
    case "exchange":
      return "Exchange";
    default:
      return value ? prettifyEnum(value) : null;
  }
}

/**
 * Net payment moving in this return. Returns:
 *   - { kind: "owed", amount } when the customer owes money (e.g. an
 *     advanced exchange where new items cost more than the return value)
 *   - { kind: "refund", amount } when the merchant owes the customer
 *   - null when neither applies
 *
 * Prefers `paymentIntents` for the "owed" case because that's the
 * authoritative record of charges the customer has been billed; falls
 * back to summed refunds for the "refund" case.
 */
export function paymentSummary(
  doc: ReturnDoc,
): { kind: "owed" | "refund"; amount: number } | null {
  const intents = doc.paymentIntents ?? [];
  let owed = 0;
  let anyIntent = false;
  for (const intent of intents) {
    const value = intent.amount;
    if (value == null) {
      continue;
    }
    const n = typeof value === "string" ? Number(value) : value;
    if (Number.isFinite(n) && n > 0) {
      owed += n;
      anyIntent = true;
    }
  }
  if (anyIntent && owed > 0) {
    return { kind: "owed", amount: owed };
  }

  const refundTotal = sumMoney(doc.refunds);
  if (refundTotal !== null && refundTotal > 0) {
    return { kind: "refund", amount: refundTotal };
  }

  return null;
}

/** Original-order placement date pulled from the enriched order summary. */
export function orderedAt(
  orders: ReturnDoc["orders"] | null | undefined,
): Date | null {
  if (!orders || orders.length === 0) {
    return null;
  }
  for (const order of orders) {
    if (order.orderedAt) {
      const date = new Date(order.orderedAt);
      if (!Number.isNaN(date.getTime())) {
        return date;
      }
    }
  }
  return null;
}
